// Helpers shared by listing carousels and detail pages.

/**
 * Slugifica um texto para uso em URLs amigáveis: minúsculas, sem acentos,
 * espaços/símbolos viram "-". Mantemos o algoritmo igual ao do backend
 * (`backend/geo.py:slugify`) para que os links públicos possam ser
 * reconstruídos no cliente sem chamada ao servidor.
 */
/** Opções de serviço vindas de GET /options/services (admin). */
interface ServiceOption {
  name: string;
}

export function mapServiceSelectOptions(items: ServiceOption[] = []): { label: string; value: string }[] {
  return items.map((s) => ({ label: s.name, value: s.name }));
}

export function slugify(text: unknown): string {
  if (!text) return "";
  return String(text)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const LISTING_SHORT_ID_LEN = 8;
/** Só gera sufixo para URL amigável quando o id é UUID (32 hex); senão "". */
const UUID_HEX_LEN = 32;

/** Sufixo curto e estável extraído do UUID do anúncio (apenas hex). */
export function listingShortId(id: unknown): string {
  if (!id) return "";
  const cleaned = String(id).replace(/[^0-9a-fA-F]/g, "").toLowerCase();
  if (cleaned.length < UUID_HEX_LEN) return "";
  return cleaned.slice(0, LISTING_SHORT_ID_LEN);
}

/**
 * Constrói a URL pública amigável de um anúncio:
 * `/perfil/{state}/{city}/{name-slug}-{short_id}`.
 *
 * Quando o backend já entrega o `path` enriquecido, usamos diretamente —
 * é a fonte da verdade. Caso contrário (ex.: anúncio antigo, edge case),
 * tentamos derivar dos campos disponíveis. Como último recurso voltamos
 * à URL legada `/acompanhante/{id}` para garantir que o link funcione.
 */
export interface ListingLike {
  path?: string;
  state_slug?: string;
  state?: string;
  city_slug?: string;
  city?: string;
  name_slug?: string;
  name?: string;
  short_id?: string;
  id?: string;
  featured?: boolean;
  featured_remaining_minutes?: number;
  bumped_remaining_minutes?: number;
  created_at?: string;
  updated_at?: string;
  featured_until?: string;
  bumped_at?: string;
  user_verified?: boolean;
  verified_at?: string | null;
  videos?: unknown[];
  cover_photo?: string;
  photos?: string[];
  user_display_name?: string;
  user_last_active_at?: string;
  response_time_avg_min?: number;
  top_score?: number;
  avg_stars?: number;
  total_reviews?: number;
  whatsapp?: string;
  phone?: string;
  services?: Array<{ title: string; highlight?: boolean }> | null;
  starting_price?: number;
  price_max?: number;
  price_ranges?: Array<{ label: string; value: string }>;
  short_bio?: string;
  user_age?: number;
  distance_km?: number;
  lat?: number | null;
  lng?: number | null;
  show_city?: boolean;
  neighborhood?: string;
  is_online?: boolean;
  description?: string;
  total_views?: number;
  days_online?: number;
}

export function buildListingPath(listing: ListingLike | null | undefined): string {
  if (!listing) return "";
  if (typeof listing.path === "string" && listing.path.startsWith("/")) {
    return listing.path;
  }
  const stateSlug = (listing.state_slug || listing.state || "").toString().toLowerCase().trim();
  const citySlug = listing.city_slug || slugify(listing.city);
  const nameSlug = listing.name_slug || slugify(listing.name);
  const shortId = listing.short_id || listingShortId(listing.id);
  if (stateSlug && citySlug && nameSlug && shortId) {
    return `/perfil/${stateSlug}/${citySlug}/${nameSlug}-${shortId}`;
  }
  if (listing.id) {
    return `/acompanhante/${listing.id}`;
  }
  return "";
}

/** Featured (Destaque) is active when the flag is on or there is featured time left. */
export function isFeaturedActive(listing: ListingLike | null | undefined): boolean {
  if (!listing) return false;
  const remaining =
    typeof listing.featured_remaining_minutes === "number"
      ? listing.featured_remaining_minutes
      : 0;
  return !!listing.featured || remaining > 0;
}

/** Boost (Forfada) is active while there are minutes left on the timer. */
export function isBoostActive(listing: ListingLike | null | undefined): boolean {
  if (!listing) return false;
  return (listing.bumped_remaining_minutes || 0) > 0;
}

const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;

export function isNewListing(listing: ListingLike | null | undefined, nowMs = Date.now()): boolean {
  if (!listing?.created_at) return false;
  const created = new Date(listing.created_at).getTime();
  if (Number.isNaN(created)) return false;
  return nowMs - created < FORTY_EIGHT_HOURS_MS;
}

const byMostRecent = (a: ListingLike, b: ListingLike) =>
  new Date(b?.featured_until || b?.bumped_at || b?.created_at || 0).getTime() -
  new Date(a?.featured_until || a?.bumped_at || a?.created_at || 0).getTime();

/**
 * Curated showcase of up to 8 cards for mobile carousels:
 *   • 2 most recent in destaque (Featured)
 *   • 4 most recent on Forfada (Boost) that aren't already featured
 *   • 2 most recent newcomers (no boost / no destaque)
 *
 * Falls back to most-recent across the remaining pool whenever any of the
 * buckets is short, so we always emit up to 8 entries when possible.
 */
export function pickShowcaseEight(items: unknown, max = 8): ListingLike[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  const listingItems = items as ListingLike[];
  const featured = listingItems.filter(isFeaturedActive).sort(byMostRecent);
  const boost = listingItems
    .filter((l) => !isFeaturedActive(l) && isBoostActive(l))
    .sort(byMostRecent);
  const fresh = listingItems
    .filter((l) => !isFeaturedActive(l) && !isBoostActive(l))
    .sort(byMostRecent);

  const picked: ListingLike[] = [];
  const seen = new Set<string | undefined>();
  const push = (l: ListingLike | undefined) => {
    if (!l || seen.has(l.id)) return;
    seen.add(l.id);
    picked.push(l);
  };

  featured.slice(0, 2).forEach(push);
  boost.slice(0, 4).forEach(push);
  fresh.slice(0, 2).forEach(push);

  if (picked.length < max) {
    const fillers = [...featured, ...boost, ...fresh].sort(byMostRecent);
    for (const l of fillers) {
      if (picked.length >= max) break;
      push(l);
    }
  }

  return picked.slice(0, max);
}

const RTF =
  typeof Intl !== "undefined" && typeof Intl.RelativeTimeFormat === "function"
    ? new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" })
    : null;

/** Compact relative time helper, e.g. "há 2 horas", "há 3 dias", "ontem". */
export function formatRelativeTime(input: unknown, nowMs = Date.now()): string | null {
  if (!input) return null;
  const ts = input instanceof Date ? input.getTime() : new Date(input as string).getTime();
  if (Number.isNaN(ts)) return null;
  const diffSec = Math.round((ts - nowMs) / 1000);
  const abs = Math.abs(diffSec);

  const ranges = [
    { unit: "second", limit: 60, divisor: 1 },
    { unit: "minute", limit: 3600, divisor: 60 },
    { unit: "hour", limit: 86_400, divisor: 3600 },
    { unit: "day", limit: 604_800, divisor: 86_400 },
    { unit: "week", limit: 2_629_800, divisor: 604_800 },
    { unit: "month", limit: 31_557_600, divisor: 2_629_800 },
    { unit: "year", limit: Infinity, divisor: 31_557_600 },
  ] as const;

  for (const range of ranges) {
    if (abs < range.limit) {
      const value = Math.round(diffSec / range.divisor);
      if (RTF) return RTF.format(value, range.unit);
      return value <= 0 ? `há ${Math.abs(value)} ${range.unit}s` : `em ${value} ${range.unit}s`;
    }
  }
  return null;
}

/**
 * Selo de atividade do anúncio para os cartões. Resumo curto que cabe em
 * uma badge:
 *   • menos de 1 minuto: "Agora"
 *   • até 1 hora: "Há X min"
 *   • mesmo dia (calendário local): "Há Xh"
 *   • dia anterior: "Ontem"
 *   • mais de 1 dia: "Mais de 1 dia"
 *
 * Recebe ISO string (`user_last_active_at` / `updated_at`).
 * Retorna `null` quando a entrada é inválida ou ausente.
 */
export function formatLastActivityBadge(input: unknown, nowMs = Date.now()): string | null {
  if (!input) return null;
  const ts = input instanceof Date ? input.getTime() : new Date(input as string).getTime();
  if (Number.isNaN(ts)) return null;
  const diffMs = nowMs - ts;
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMs < 60_000) return "Agora";
  if (diffMin < 60) return `há ${diffMin} minutos`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `há ${diffHr} horas`;
  return null;
}